// frontend/src/pages/LandingPage/DepoimentosPage.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    Star, 
    Quote, 
    User,
    Building2,
    Heart,
    CheckCircle2
} from 'lucide-react';
import HeaderPublic from '../../components/Navigation/HeaderPublic';
import Footer from '../../components/LandingPage/Footer';

const DepoimentosPage = () => {
    // Garante que a página sempre comece no topo ao carregar
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    // Dados mockados de depoimentos
    const depoimentos = [
        {
            id: 1,
            nome: 'Dra. Maria Silva',
            cargo: 'Diretora de Enfermagem',
            organizacao: 'Casa de Repouso Bem-Estar',
            cidade: 'Porto Alegre, RS',
            foto: null, // Pode adicionar fotos depois
            avaliacao: 5,
            texto: 'O Higeia.tech transformou completamente nossa gestão. Antes, perdíamos horas com planilhas e papéis. Agora, tudo está centralizado e acessível. A funcionalidade de prontuário eletrônico é excepcional e o módulo financeiro nos ajudou a ter controle total sobre os custos.',
            data: 'Abril 2024',
            destaque: true
        },
        {
            id: 2,
            nome: 'Carlos Eduardo Santos',
            cargo: 'Administrador',
            organizacao: 'Home Care Vida Longa',
            cidade: 'Florianópolis, SC',
            foto: null,
            avaliacao: 5,
            texto: 'Implementamos o sistema há 6 meses e os resultados são impressionantes. A redução de erros na administração de medicamentos foi de 90%. A equipe adora a interface intuitiva e os pacientes ficam mais seguros sabendo que seus dados estão bem organizados.',
            data: 'Dezembro 2024',
            destaque: false
        },
        {
            id: 3,
            nome: 'Ana Paula Costa',
            cargo: 'Coordenadora de Cuidados',
            organizacao: 'Lar dos Anjos',
            cidade: 'Curitiba, PR',
            foto: null,
            avaliacao: 5,
            texto: 'A funcionalidade de diário de cuidados mudou nossa rotina. Agora conseguimos rastrear cada cuidado prestado, gerar relatórios detalhados e garantir que nada seja esquecido. O suporte da equipe Higeia.tech também é excelente!',
            data: 'Novembro 2024',
            destaque: false
        },
        {
            id: 4,
            nome: 'Dr. Roberto Mendes',
            cargo: 'Médico Coordenador',
            organizacao: 'Clínica Geriátrica São José',
            cidade: 'São Paulo, SP',
            foto: null,
            avaliacao: 5,
            texto: 'Como médico, preciso de acesso rápido e confiável ao histórico dos pacientes. O Higeia.tech oferece exatamente isso. O prontuário é completo, organizado e me permite tomar decisões mais rápidas e precisas. Recomendo sem hesitação.',
            data: 'Março 2025',
            destaque: true
        },
        {
            id: 5,
            nome: 'Juliana Ferreira',
            cargo: 'Enfermeira Chefe',
            organizacao: 'Residencial Harmonia',
            cidade: 'Belo Horizonte, MG',
            foto: null,
            avaliacao: 5,
            texto: 'O sistema de agendamentos e ponto eletrônico simplificou muito nossa gestão de equipe. Conseguimos otimizar os turnos e garantir que sempre tenhamos a cobertura necessária. A integração entre os módulos é perfeita.',
            data: 'Setembro 2024',
            destaque: false
        },
        {
            id: 6,
            nome: 'Fernando Alves',
            cargo: 'Proprietário',
            organizacao: 'Cuidados Especiais Ltda',
            cidade: 'Rio de Janeiro, RJ',
            foto: null,
            avaliacao: 5,
            texto: 'Investir no Higeia.tech foi uma das melhores decisões que tomamos. O retorno sobre investimento foi rápido - reduzimos custos operacionais em 25% e aumentamos a satisfação dos clientes. O módulo financeiro nos deu visibilidade total.',
            data: 'Agosto 2024',
            destaque: false
        },
        {
            id: 7,
            nome: 'Patrícia Lima',
            cargo: 'Assistente Social',
            organizacao: 'Centro de Acolhimento Esperança',
            cidade: 'Salvador, BA',
            foto: null,
            avaliacao: 5,
            texto: 'A transparência que o sistema oferece para as famílias é incrível. Elas podem acompanhar o cuidado de seus entes queridos em tempo real, o que gera muita confiança. A segurança dos dados também é um diferencial importante.',
            data: 'Fevereiro 2025',
            destaque: false
        },
        {
            id: 8,
            nome: 'Marcos Antônio',
            cargo: 'Gerente Operacional',
            organizacao: 'Home Care Premium',
            cidade: 'Brasília, DF',
            foto: null,
            avaliacao: 5,
            texto: 'A automação de tarefas recorrentes nos poupou muito tempo. Alertas de medicação, agendamentos automáticos e relatórios gerados automaticamente fazem toda a diferença. A equipe pode focar no que realmente importa: o cuidado.',
            data: 'Janeiro 2025',
            destaque: true
        },
        {
            id: 9,
            nome: 'Lucia Helena',
            cargo: 'Coordenadora Administrativa',
            organizacao: 'Vila dos Idosos',
            cidade: 'Recife, PE',
            foto: null,
            avaliacao: 5,
            texto: 'A curva de aprendizado foi muito rápida. Em uma semana toda a equipe já estava usando o sistema com confiança. Os treinamentos oferecidos foram excelentes e o suporte contínuo faz toda a diferença.',
            data: 'Julho 2024',
            destaque: false
        }
    ];

    // Função para renderizar estrelas
    const renderStars = (rating) => {
        return Array.from({ length: 5 }, (_, index) => (
            <Star
                key={index}
                className={`h-5 w-5 ${
                    index < rating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                }`}
            />
        ));
    };

    // Separar depoimentos em destaque e normais
    const depoimentosDestaque = depoimentos.filter(d => d.destaque);
    const depoimentosNormais = depoimentos.filter(d => !d.destaque);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Público */}
            <div className="bg-white shadow-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <HeaderPublic />
                </div>
            </div>

            {/* Conteúdo Principal */}
            <main>
                {/* Hero Section */}
                <motion.section
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative py-20 sm:py-24 bg-gradient-to-br from-higeia-dark via-higeia-medium to-higeia-dark text-white overflow-hidden"
                >
                    {/* Padrão decorativo */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }} />
                    </div>

                    <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6"
                        >
                            <Quote className="h-10 w-10" />
                        </motion.div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
                            O Que Nossos{' '}
                            <span className="bg-gradient-to-r from-white to-higeia-light bg-clip-text text-transparent">
                                Clientes Dizem
                            </span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                            Conheça as experiências reais de profissionais e organizações que transformaram sua gestão de cuidados com o Higeia.tech
                        </p>
                        
                        {/* Estatísticas Rápidas */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                            className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto"
                        >
                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                                <div className="text-3xl font-black mb-1">5.0</div>
                                <div className="flex justify-center gap-1 mb-2">
                                    {renderStars(5)}
                                </div>
                                <div className="text-sm text-white/80">Avaliação Média</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                                <div className="text-3xl font-black mb-1">100%</div>
                                <div className="text-sm text-white/80">Satisfação</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                                <div className="text-3xl font-black mb-1">9+</div>
                                <div className="text-sm text-white/80">Depoimentos</div>
                            </div>
                        </motion.div>
                    </div>
                </motion.section>

                {/* Seção: Depoimentos em Destaque */}
                {depoimentosDestaque.length > 0 && (
                    <section className="py-16 sm:py-20 bg-white">
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                                className="text-center mb-12"
                            >
                                <div className="inline-flex items-center gap-2 bg-higeia-light/10 text-higeia-dark px-4 py-2 rounded-full mb-4">
                                    <CheckCircle2 className="h-5 w-5" />
                                    <span className="font-bold text-sm">DESTAQUES</span>
                                </div>
                                <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">
                                    Depoimentos em{' '}
                                    <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                                        Destaque
                                    </span>
                                </h2>
                            </motion.div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {depoimentosDestaque.map((depoimento, index) => (
                                    <motion.div
                                        key={depoimento.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.2, duration: 0.6 }}
                                        className="relative bg-gradient-to-br from-higeia-dark to-higeia-medium rounded-3xl p-8 text-white shadow-2xl"
                                    >
                                        {/* Ícone de aspas decorativo */}
                                        <Quote className="absolute top-6 right-6 h-16 w-16 text-white/10" />
                                        
                                        {/* Avaliação com estrelas */}
                                        <div className="flex gap-1 mb-4">
                                            {renderStars(depoimento.avaliacao)}
                                        </div>

                                        {/* Texto do depoimento */}
                                        <p className="text-lg leading-relaxed mb-6 relative z-10">
                                            "{depoimento.texto}"
                                        </p>

                                        {/* Informações do autor */}
                                        <div className="flex items-center gap-4 pt-6 border-t border-white/20">
                                            <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                                                <User className="h-6 w-6" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-bold text-lg">{depoimento.nome}</div>
                                                <div className="text-white/80 text-sm">{depoimento.cargo}</div>
                                                <div className="flex items-center gap-2 text-white/70 text-sm mt-1">
                                                    <Building2 className="h-4 w-4" />
                                                    <span>{depoimento.organizacao}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Data */}
                                        <div className="absolute bottom-4 right-6 text-white/60 text-sm">
                                            {depoimento.data}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Seção: Todos os Depoimentos */}
                <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="text-center mb-12"
                        >
                            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">
                                Mais{' '}
                                <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                                    Depoimentos
                                </span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                Veja o que outros profissionais estão dizendo sobre o Higeia.tech
                            </p>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {depoimentosNormais.map((depoimento, index) => (
                                <motion.div
                                    key={depoimento.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1, duration: 0.6 }}
                                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                                >
                                    {/* Avaliação com estrelas */}
                                    <div className="flex gap-1 mb-4">
                                        {renderStars(depoimento.avaliacao)}
                                    </div>

                                    {/* Texto do depoimento */}
                                    <p className="text-gray-700 leading-relaxed mb-6">
                                        "{depoimento.texto}"
                                    </p>

                                    {/* Informações do autor */}
                                    <div className="flex items-center gap-3 pt-6 border-t border-gray-100">
                                        <div className="flex-shrink-0 w-10 h-10 bg-higeia-light/20 rounded-full flex items-center justify-center group-hover:bg-higeia-light/30 transition-colors">
                                            <User className="h-5 w-5 text-higeia-dark" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-gray-900 truncate">{depoimento.nome}</div>
                                            <div className="text-gray-600 text-sm truncate">{depoimento.cargo}</div>
                                            <div className="flex items-center gap-1 text-gray-500 text-xs mt-1">
                                                <Building2 className="h-3 w-3" />
                                                <span className="truncate">{depoimento.organizacao}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Data */}
                                    <div className="text-gray-400 text-xs mt-4">
                                        {depoimento.data}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Seção: CTA Final */}
                <section className="py-16 sm:py-20 bg-gradient-to-br from-higeia-dark to-higeia-medium">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                                <Heart className="h-8 w-8 text-white" />
                            </div>
                            <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
                                Faça Parte Desta{' '}
                                <span className="bg-gradient-to-r from-white to-higeia-light bg-clip-text text-transparent">
                                    Comunidade
                                </span>
                            </h2>
                            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                                Junte-se a centenas de profissionais que já transformaram sua gestão de cuidados com o Higeia.tech
                            </p>
                            <motion.a
                                href="/demonstracao"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-block bg-white text-higeia-dark px-8 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                            >
                                Começar Agora
                            </motion.a>
                        </motion.div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default DepoimentosPage;

